import { apiFetch } from './apiClient.js';

export type InboundTriggerType = 'always' | 'keyword' | 'first_message';
export type InboundCampaignStatus = 'active' | 'paused' | 'archived';

export interface InboundCampaign {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  channelId: string;
  flowId: string;
  triggerType: InboundTriggerType;
  keywords?: string[];
  welcomeMessage?: string;
  status: InboundCampaignStatus;
  priority: number;
  conversationsStarted: number;
  version: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface InboundCampaignCreate {
  name: string;
  description?: string;
  channelId: string;
  flowId: string;
  triggerType: InboundTriggerType;
  keywords?: string[];
  welcomeMessage?: string;
  status?: InboundCampaignStatus;
  priority?: number;
}

export type InboundCampaignPatch = Partial<InboundCampaignCreate>;

export async function listInboundCampaigns(
  tenantId: string,
  opts: { includeArchived?: boolean; status?: InboundCampaignStatus } = {},
): Promise<InboundCampaign[]> {
  const params: string[] = [];
  if (opts.includeArchived) params.push('includeArchived=true');
  if (opts.status) params.push(`status=${encodeURIComponent(opts.status)}`);
  const qs = params.length ? `?${params.join('&')}` : '';
  const res = await apiFetch<{ items: InboundCampaign[] }>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/inbound-campaigns${qs}`,
  );
  return res.items;
}

export async function createInboundCampaign(
  tenantId: string,
  body: InboundCampaignCreate,
): Promise<InboundCampaign> {
  return apiFetch<InboundCampaign>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/inbound-campaigns`,
    { method: 'POST', body },
  );
}

export async function updateInboundCampaign(
  tenantId: string,
  id: string,
  patch: InboundCampaignPatch,
  ifMatchVersion: number,
): Promise<InboundCampaign> {
  return apiFetch<InboundCampaign>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/inbound-campaigns/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body: patch,
      headers: { 'If-Match': String(ifMatchVersion) },
    },
  );
}

export async function archiveInboundCampaign(
  tenantId: string,
  id: string,
): Promise<InboundCampaign> {
  return apiFetch<InboundCampaign>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/inbound-campaigns/${encodeURIComponent(id)}/archive`,
    { method: 'POST' },
  );
}
